import { useEffect, useMemo, useState } from 'react';
import { LOCALE_NAMES } from './locales';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import {
  desktopPermission,
  requestDesktopPermission,
  type DesktopPermission,
} from '../app/desktop-notifications';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { playMessageSound } from '../app/sounds';
import { usePreferences } from '../app/preferences-store';
import { formatDateTime } from '../ui/format';
import type { DateFormat, TimeFormat } from '../ui/format';
import { Badge, PageHeader, Panel } from '../ui/Page';
import {
  COMMON_ZONES,
  NOTIFICATION_KINDS,
  describeZone,
  listTimeZones,
  saveProfile,
  type NotificationKind,
} from './preferences-api';
import {
  currentPushState,
  disablePush,
  enablePush,
  getPushStatus,
  type PushState,
} from '../app/web-push';

/**
 * Preferences (context.md sections 130, 132, 206).
 *
 * Everything here is this person's, not the company's. A timezone on a screen headed "Configurações"
 * that quietly changed the zone for everybody would be a trap, and the one place a tenant-wide zone
 * genuinely matters — when a campaign's sending window opens — is decided on the server from the
 * tenant's own setting, never from whatever a browser happens to be in.
 *
 * The regional settings are saved as they are changed, because there is nothing to get wrong: every
 * one is a closed set, and the interface reformats in front of you. The security panel is different
 * — changing a password is not something to do by accident — and lives behind the account screen.
 */

const DATE_FORMATS: DateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
const TIME_FORMATS: TimeFormat[] = ['24h', '12h'];
const LOCALES = Object.entries(LOCALE_NAMES).map(([value, label]) => ({ value, label }));

export function PreferencesPage(): React.JSX.Element {
  const { user, refreshUser } = useAuth();
  const { preferences, set } = usePreferences();

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [notifications, setNotifications] = useState<Record<string, boolean>>(
    user?.email_notifications ?? {},
  );

  useEffect(() => {
    if (user?.email_notifications) setNotifications(user.email_notifications);
  }, [user?.email_notifications]);

  const [pushKinds, setPushKinds] = useState<Record<string, boolean>>(
    user?.push_notifications ?? {},
  );

  useEffect(() => {
    if (user?.push_notifications) setPushKinds(user.push_notifications);
  }, [user?.push_notifications]);

  // The full list is only needed when somebody looks past the common zones, so it is fetched with
  // the screen rather than bundled: four hundred names is not something to ship to everybody.
  const zones = useQuery({ queryKey: ['timezones'], queryFn: listTimeZones, staleTime: Infinity });

  const zoneOptions = useMemo(() => {
    const all = zones.data?.timezones ?? [];
    const rest = all.filter((zone) => !COMMON_ZONES.includes(zone)).sort();

    return { common: COMMON_ZONES.filter((zone) => all.length === 0 || all.includes(zone)), rest };
  }, [zones.data]);

  const save = useMutation({
    mutationFn: saveProfile,
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await refreshUser();
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(messageFor(caught));
    },
  });

  // The save confirmation fades on its own: a banner that stays is a banner people stop reading.
  useEffect(() => {
    if (!saved) return;

    const timer = setTimeout(() => setSaved(false), 2500);

    return () => clearTimeout(timer);
  }, [saved]);

  function toggle(kind: NotificationKind, value: boolean): void {
    const next = { ...notifications, [kind]: value };

    setNotifications(next);
    save.mutate({ emailNotifications: next as Partial<Record<NotificationKind, boolean>> });
  }

  function togglePush(kind: NotificationKind, value: boolean): void {
    const next = { ...pushKinds, [kind]: value };

    setPushKinds(next);
    save.mutate({ pushNotifications: next as Partial<Record<NotificationKind, boolean>> });
  }

  const zone = user?.timezone ?? '';

  return (
    <div className="view">
      <PageHeader
        title={t('prefs.title')}
        description={t('prefs.description')}
        actions={
          save.isPending ? (
            <span className="muted small">{t('common.saving')}</span>
          ) : saved ? (
            <span className="muted small" role="status">
              {t('prefs.saved')}
            </span>
          ) : undefined
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel title={t('prefs.regional')}>
        <div className="prefs">
          <Setting label={t('prefs.timezone')} hint={t('prefs.timezoneHint')}>
            <select
              className="select"
              value={zone}
              onChange={(event) => save.mutate({ timezone: event.target.value })}
            >
              <optgroup label={t('prefs.commonZones')}>
                {zoneOptions.common.map((each) => (
                  <option key={each} value={each}>
                    {describeZone(each, user?.locale)}
                  </option>
                ))}
              </optgroup>

              {zoneOptions.rest.length > 0 && (
                <optgroup label={t('prefs.allZones')}>
                  {zoneOptions.rest.map((each) => (
                    <option key={each} value={each}>
                      {each}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </Setting>

          <Setting label={t('prefs.language')} hint={t('prefs.languageHint')}>
            <select
              className="select"
              value={user?.locale ?? 'pt-BR'}
              onChange={(event) => save.mutate({ locale: event.target.value })}
            >
              {LOCALES.map((locale) => (
                <option key={locale.value} value={locale.value}>
                  {locale.label}
                </option>
              ))}
            </select>

            {user?.locale && user.locale !== 'pt-BR' && (
              <p className="muted small">{t('prefs.languagePartial')}</p>
            )}
          </Setting>

          <Setting label={t('prefs.dateFormat')} hint={t('prefs.dateFormatHint')}>
            <select
              className="select"
              value={preferences.dateFormat}
              onChange={(event) => {
                const value = event.target.value as DateFormat;

                set('dateFormat', value);
                save.mutate({ dateFormat: value });
              }}
            >
              {DATE_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {format} ({sample(format, preferences.timeFormat).date})
                </option>
              ))}
            </select>
          </Setting>

          <Setting label={t('prefs.timeFormat')} hint={t('prefs.timeFormatHint')}>
            <select
              className="select"
              value={preferences.timeFormat}
              onChange={(event) => {
                const value = event.target.value as TimeFormat;

                set('timeFormat', value);
                save.mutate({ timeFormat: value });
              }}
            >
              {TIME_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {t(`prefs.time.${format}`)} ({sample(preferences.dateFormat, format).time})
                </option>
              ))}
            </select>
          </Setting>
        </div>
      </Panel>

      <Panel title={t('prefs.appearance')}>
        <div className="settings">
          <Setting label={t('prefs.theme')} hint={t('prefs.themeHint')}>
            <div className="chips">
              {(['system', 'light', 'dark'] as const).map((theme) => (
                <button
                  key={theme}
                  type="button"
                  className={`chip${preferences.theme === theme ? ' chip--active' : ''}`}
                  onClick={() => set('theme', theme)}
                >
                  {t(`prefs.theme.${theme}`)}
                </button>
              ))}
            </div>
          </Setting>
          <Setting label={t('prefs.toasts')} hint={t('prefs.toastsHint')}>
            <label className="cell-inline">
              <input
                type="checkbox"
                checked={preferences.toasts}
                onChange={(event) => set('toasts', event.target.checked)}
              />
              {t('prefs.toastsOn')}
            </label>
          </Setting>
          <Setting label={t('prefs.sounds')} hint={t('prefs.soundsHint')}>
            <label className="cell-inline">
              <input
                type="checkbox"
                checked={preferences.sounds}
                onChange={(event) => {
                  set('sounds', event.target.checked);

                  // Played on the way on, never on the way off: it is the only way to know what it
                  // sounds like without waiting for a customer to write.
                  if (event.target.checked) playMessageSound();
                }}
              />
              {t('prefs.soundsOn')}
            </label>
          </Setting>
          <Setting label={t('prefs.tables')} hint={t('prefs.tablesHint')}>
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={Object.keys(preferences.tables).length === 0}
              onClick={() => set('tables', {})}
            >
              {t('prefs.resetTables')}
            </button>
          </Setting>
        </div>
      </Panel>

      <Panel title={t('prefs.emailNotifications')}>
        <div className="prefs">
          {NOTIFICATION_KINDS.map((kind) => (
            <label key={kind} className="prefs__switch">
              <input
                type="checkbox"
                checked={notifications[kind] ?? false}
                onChange={(event) => toggle(kind, event.target.checked)}
              />

              <span className="prefs__switch-text">
                <span className="strong">{t(`prefs.notify.${kind}`)}</span>
                <span className="muted small">{t(`prefs.notify.${kind}.hint`)}</span>
              </span>
            </label>
          ))}
        </div>

        <p className="muted small">{t('prefs.emailNotificationsNote')}</p>
      </Panel>

      <Panel title={t('prefs.desktopNotifications')}>
        <DesktopNotifications />
      </Panel>

      <PushNotifications kinds={pushKinds} onToggle={togglePush} />

      <Panel title={t('prefs.security')}>
        <dl className="details">
          <dt>{t('prefs.lastPasswordChange')}</dt>
          <dd>
            {user?.password_changed_at ? (
              formatDateTime(user.password_changed_at)
            ) : (
              <Badge tone="warning">{t('prefs.passwordNeverChanged')}</Badge>
            )}
          </dd>

          <dt>{t('prefs.twoFactor')}</dt>
          <dd>
            <Badge tone="neutral">{t('prefs.comingSoon')}</Badge>{' '}
            <span className="muted small">{t('prefs.twoFactorHint')}</span>
          </dd>
        </dl>

        <Link className="button button--quiet button--compact" to="/configuracoes/conta">
          {t('prefs.goToAccount')}
        </Link>
      </Panel>
    </div>
  );
}

function Setting({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="prefs__row">
      <div className="prefs__label">
        <span className="strong">{label}</span>
        <span className="muted small">{hint}</span>
      </div>

      <div className="prefs__control">{children}</div>
    </div>
  );
}

/**
 * What each option looks like, using a fixed moment rather than now.
 *
 * A live clock in a `<select>` would make the option labels change while somebody reads them, and a
 * date whose day and month differ is the only one that shows what the order actually does.
 */
function sample(dateFormat: DateFormat, timeFormat: TimeFormat): { date: string; time: string } {
  const moment = new Date(Date.UTC(2026, 8, 18, 17, 15));

  const year = '2026';
  const month = '09';
  const day = '18';

  const date =
    dateFormat === 'MM/DD/YYYY'
      ? `${month}/${day}/${year}`
      : dateFormat === 'YYYY-MM-DD'
        ? `${year}-${month}-${day}`
        : `${day}/${month}/${year}`;

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
    timeZone: 'UTC',
  }).format(moment);

  return { date, time };
}

/**
 * The pop-up that reaches somebody who is not looking at the product.
 *
 * The permission is asked for **here**, from a switch somebody just turned on, and never on page
 * load. A prompt nobody invited is the one everyone has learned to block, and a block is permanent —
 * which would take the feature away from them for good, from a decision they made in half a second
 * about a product they had just opened.
 *
 * The three answers are different states and the panel says which: not asked yet, allowed, or
 * refused. "Refused" is not an error to retry; it is a setting only the browser can undo, so the
 * text says where to go rather than offering a button that would do nothing.
 */
function DesktopNotifications(): React.JSX.Element {
  const [permission, setPermission] = useState<DesktopPermission>(() => desktopPermission());

  if (permission === 'unsupported') {
    return <p className="muted small">{t('prefs.desktopUnsupported')}</p>;
  }

  return (
    <div className="stack">
      <p className="muted small">{t('prefs.desktopHint')}</p>

      {permission === 'granted' && (
        <p className="small">
          <Badge tone="positive">{t('prefs.desktopOn')}</Badge>
        </p>
      )}

      {permission === 'denied' && <p className="muted small">{t('prefs.desktopBlocked')}</p>}

      {permission === 'default' && (
        <button
          type="button"
          className="button button--compact"
          onClick={() => void requestDesktopPermission().then(setPermission)}
        >
          {t('prefs.desktopEnable')}
        </button>
      )}
    </div>
  );
}

/**
 * Notifications that reach a phone (Phase 119).
 *
 * The switch asks for permission when it is turned on, never on load: a prompt nobody invited is
 * the one everybody blocks, and a block is permanent — it would take the feature away for good over
 * a decision made in half a second.
 *
 * The panel does not exist at all when the installation has no VAPID pair. A switch that cannot do
 * anything is worse than a missing feature: somebody turns it on, nothing arrives, and they
 * conclude the product's notifications do not work.
 */
function PushNotifications({
  kinds,
  onToggle,
}: {
  kinds: Record<string, boolean>;
  onToggle: (kind: NotificationKind, value: boolean) => void;
}): React.JSX.Element | null {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const status = useQuery({ queryKey: ['push-status'], queryFn: getPushStatus, retry: false });

  useEffect(() => {
    void currentPushState().then(setState);
  }, []);

  if (status.isError || status.data?.available === false) return null;

  const publicKey = status.data?.publicKey ?? null;

  async function change(on: boolean): Promise<void> {
    setBusy(true);
    setFailed(null);

    try {
      setState(on ? await enablePush(publicKey!) : await disablePush());
      await status.refetch();
    } catch (caught) {
      setFailed(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={t('prefs.pushNotifications')}>
      <div className="stack">
        <p className="muted small">{t('prefs.pushHint')}</p>

        {failed && (
          <p className="alert" role="alert">
            {failed}
          </p>
        )}

        {state === 'unsupported' && <p className="muted small">{t('prefs.pushUnsupported')}</p>}

        {/* Blocked is not an error to retry: only the browser can undo it, so this says where. */}
        {state === 'denied' && <p className="muted small">{t('prefs.pushBlocked')}</p>}

        {state === 'on' && (
          <p className="small">
            <Badge tone="positive">{t('prefs.pushOn')}</Badge>{' '}
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={busy}
              onClick={() => void change(false)}
            >
              {t('prefs.pushDisable')}
            </button>
          </p>
        )}

        {state === 'off' && publicKey && (
          <button
            type="button"
            className="button button--compact"
            disabled={busy}
            onClick={() => void change(true)}
          >
            {t('prefs.pushEnable')}
          </button>
        )}

        {/* Which kinds, only once there is somewhere for them to arrive. */}
        {state === 'on' && (
          <div className="prefs">
            {NOTIFICATION_KINDS.map((kind) => (
              <label key={kind} className="prefs__switch">
                <input
                  type="checkbox"
                  checked={kinds[kind] ?? false}
                  onChange={(event) => onToggle(kind, event.target.checked)}
                />

                <span className="prefs__switch-text">
                  <span className="strong">{t(`prefs.notify.${kind}`)}</span>
                  <span className="muted small">{t(`prefs.notify.${kind}.hint`)}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Every browser this person has said yes in, so they can tell one from another. */}
        {(status.data?.devices?.length ?? 0) > 0 && (
          <ul className="muted small">
            {status.data!.devices!.map((device) => (
              <li key={device.id}>
                {device.userAgent ?? device.endpoint.slice(0, 40)}
                {device.lastSentAt &&
                  ` · ${t('prefs.pushLastSent').replace('{when}', formatDateTime(device.lastSentAt))}`}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
